import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { usePublicationRuns } from '../../hooks';
import { formatDateTime, formatNumber } from '../../lib/format';
import {
  publicationRunStatusColor,
  publicationRunStatusLabel,
  publicationRunTriggerLabel,
} from '../../model/options';
import { PublicationRunDetailDrawer } from './PublicationRunDetailDrawer';

const PAGE_SIZE = 20;

/** Publication-run history for a catalog (server-paged) → a run detail drawer per row. */
export function PublicationRunsPanel({ catalogId }: { catalogId: string }) {
  const { t } = useTranslation('catalog');
  const [page, setPage] = useState(0);
  const runsQuery = usePublicationRuns(catalogId, { page, size: PAGE_SIZE });
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  if (runsQuery.isLoading) {
    return <LoadingState label={t('publicationRunsPanel.loading')} />;
  }
  if (runsQuery.isError) {
    return <ErrorState message={runsQuery.error?.message} onRetry={() => void runsQuery.refetch()} />;
  }

  const runs = runsQuery.data?.content ?? [];
  const total = runsQuery.data?.totalElements ?? 0;

  return (
    <>
      {runs.length === 0 ? (
        <EmptyState
          title={t('publicationRunsPanel.empty.title')}
          description={t('publicationRunsPanel.empty.description')}
        />
      ) : (
        <Card variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('publicationRunsPanel.table.trigger')}</TableCell>
                <TableCell>{t('publicationRunsPanel.table.status')}</TableCell>
                <TableCell align="right">{t('publicationRunsPanel.table.evaluated')}</TableCell>
                <TableCell align="right">{t('publicationRunsPanel.table.included')}</TableCell>
                <TableCell align="right">{t('publicationRunsPanel.table.excluded')}</TableCell>
                <TableCell>{t('publicationRunsPanel.table.startedAt')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id} hover onClick={() => setSelectedRunId(run.id)} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={publicationRunTriggerLabel(t, run.trigger)} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={publicationRunStatusColor(run.status)}
                      label={publicationRunStatusLabel(t, run.status)}
                    />
                  </TableCell>
                  <TableCell align="right">{formatNumber(run.productsEvaluated)}</TableCell>
                  <TableCell align="right">{formatNumber(run.productsIncluded)}</TableCell>
                  <TableCell align="right">{formatNumber(run.productsExcluded)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatDateTime(run.startedAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <CompactTablePagination
            count={total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            hideRowsPerPage
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={() => {}}
          />
        </Card>
      )}

      <PublicationRunDetailDrawer
        open={selectedRunId !== null}
        onClose={() => setSelectedRunId(null)}
        catalogId={catalogId}
        runId={selectedRunId}
      />
    </>
  );
}
